/**
 * Survey Responses Page - View all responses for a specific survey
 * 
 * Connected to backend API:
 * - GET /api/v1/supplier-portal/surveys/:id/responses
 * - POST /api/v1/supplier-portal/surveys/:id/responses/:responseId/approve
 * - POST /api/v1/supplier-portal/surveys/:id/responses/:responseId/reject
 */
import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft,
  ChevronRight,
  Globe,
  Users,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  AlertCircle,
  Eye,
  Download,
  Filter,
  Search,
  Check,
  X,
  MessageSquare,
  FileText,
  Shield,
  BarChart3,
  TrendingUp
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
import { apiFetch } from '../../../services/api_service';

export default function SurveyResponsesPage() {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  
  const [survey, setSurvey] = useState(null);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [processingResponse, setProcessingResponse] = useState(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Approval Modal
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalComments, setApprovalComments] = useState('');
  const [approvalAction, setApprovalAction] = useState(null);

  useEffect(() => {
    if (surveyId) {
      fetchData();
    }
  }, [surveyId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [surveyData, responsesData] = await Promise.all([
        apiFetch(`/supplier-portal/surveys/${surveyId}`),
        apiFetch(`/supplier-portal/surveys/${surveyId}/responses`)
      ]);

      if (surveyData?._error) throw new Error(surveyData.message || 'Failed to fetch survey');
      setSurvey(surveyData);

      if (!responsesData?._error) {
        setResponses(responsesData.responses || []);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleApproval = async (responseId, action, comments = '') => {
    try {
      setProcessingResponse(responseId);
      
      const endpoint = action === 'approve' 
        ? `/supplier-portal/surveys/${surveyId}/responses/${responseId}/approve`
        : `/supplier-portal/surveys/${surveyId}/responses/${responseId}/reject`;
      
      const data = await apiFetch(endpoint, {
        method: 'POST',
        body: { 
          comments: action === 'approve' ? comments : undefined,
          reason: action === 'reject' ? comments : undefined
        }
      });

      if (data?._error) throw new Error(data.message || `Failed to ${action} response`);
      
      fetchData();
      setShowApprovalModal(false);
      setApprovalComments('');
      setSelectedResponse(null);
    } catch (err) {
      console.error('Error processing response:', err);
      alert(err.message);
    } finally {
      setProcessingResponse(null);
    }
  };

  const openApprovalModal = (response, action) => {
    setSelectedResponse(response);
    setApprovalAction(action);
    setShowApprovalModal(true);
  };

  // Filter responses
  const filteredResponses = responses.filter(r => {
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'pending' && r.approval?.status === 'pending') ||
      (statusFilter === 'approved' && r.approval?.status === 'approved') ||
      (statusFilter === 'rejected' && r.approval?.status === 'rejected');
    
    const matchesSearch = !searchQuery || 
      r.supplierName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.supplierId?.toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesStatus && matchesSearch;
  });

  // Stats
  const stats = {
    total: responses.length,
    pending: responses.filter(r => r.approval?.status === 'pending').length,
    approved: responses.filter(r => r.approval?.status === 'approved').length,
    rejected: responses.filter(r => r.approval?.status === 'rejected').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw size={32} className="text-purple-400 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <AlertCircle size={48} className="text-red-400" />
        <p className="text-red-400">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="survey-responses-page">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-[#6B6B6B] text-sm">
        <Link to="/value-chain/supplier-portal" className="hover:text-purple-400 flex items-center gap-1">
          <Globe size={14} />
          <span>Supplier Portal</span>
        </Link>
        <ChevronRight size={14} />
        <Link to={`/value-chain/supplier-portal/surveys/${surveyId}`} className="hover:text-purple-400">
          {survey?.surveyName || 'Survey'}
        </Link>
        <ChevronRight size={14} />
        <span className="text-white">Responses</span>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Survey Responses</h1>
          <p className="text-sm text-[#6B6B6B] mt-1">{survey?.surveyName}</p>
        </div>
        
        <div className="flex items-center gap-3">
          <button className="flex items-center gap-2 px-4 py-2 bg-[#1F1F1F] text-[#A0A0A0] rounded-lg hover:bg-[#2F2F2F]">
            <Download size={16} />
            Export All
          </button>
          <button
            onClick={() => navigate(`/value-chain/supplier-portal/surveys/${surveyId}`)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-500/20 border border-purple-500/30 text-purple-400 rounded-lg hover:bg-purple-500/30"
          >
            <ArrowLeft size={16} />
            Back to Survey
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <StatCard label="Total Responses" value={stats.total} icon={Users} color="purple" />
        <StatCard label="Pending Review" value={stats.pending} icon={Clock} color="amber" />
        <StatCard label="Approved" value={stats.approved} icon={CheckCircle} color="emerald" />
        <StatCard label="Rejected" value={stats.rejected} icon={XCircle} color="red" />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B6B]" />
          <input
            type="text"
            placeholder="Search suppliers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-[#111111] border border-[#1F1F1F] rounded-lg text-sm text-white placeholder:text-[#6B6B6B] focus:border-purple-500/50 focus:outline-none"
          />
        </div>
        
        <div className="flex items-center gap-2">
          {['all', 'pending', 'approved', 'rejected'].map(status => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-3 py-2 rounded-lg text-sm capitalize transition-colors ${
                statusFilter === status
                  ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                  : 'bg-[#111111] text-[#6B6B6B] border border-[#1F1F1F] hover:text-white'
              }`}
            >
              {status}
            </button>
          ))}
        </div>

        <button 
          onClick={fetchData}
          className="p-2.5 bg-[#111111] border border-[#1F1F1F] rounded-lg text-[#6B6B6B] hover:text-white"
        >
          <RefreshCw size={16} />
        </button>
      </div>

      {/* Responses List */}
      <div className="space-y-4">
        {filteredResponses.map(response => (
          <ResponseCard 
            key={response.id}
            response={response}
            onApprove={() => openApprovalModal(response, 'approve')}
            onReject={() => openApprovalModal(response, 'reject')}
            onView={() => setSelectedResponse(response)}
            processingResponse={processingResponse}
          />
        ))}

        {filteredResponses.length === 0 && (
          <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-12 text-center">
            <Users className="mx-auto text-[#2F2F2F] mb-4" size={48} />
            <h3 className="text-lg font-semibold text-white mb-2">No responses found</h3>
            <p className="text-sm text-[#6B6B6B]">
              {statusFilter !== 'all' ? 'Try changing the filter' : 'No suppliers have submitted responses yet'}
            </p>
          </div>
        )}
      </div>

      {/* Approval Modal */}
      {showApprovalModal && selectedResponse && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#111111] border border-[#1F1F1F] rounded-2xl w-full max-w-lg">
            <div className="p-6 border-b border-[#1F1F1F]">
              <h3 className="text-lg font-semibold text-white">
                {approvalAction === 'approve' ? 'Approve Response' : 'Reject Response'}
              </h3>
              <p className="text-sm text-[#6B6B6B] mt-1">
                {selectedResponse.supplierName}
              </p>
            </div>
            
            <div className="p-6">
              <label className="block text-sm text-[#A0A0A0] mb-2">
                {approvalAction === 'approve' ? 'Comments (optional)' : 'Rejection Reason'}
              </label>
              <textarea
                value={approvalComments}
                onChange={(e) => setApprovalComments(e.target.value)}
                placeholder={approvalAction === 'approve' ? 'Add any notes...' : 'Explain why this response is being rejected...'}
                rows={4}
                className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg text-white placeholder:text-[#6B6B6B] focus:border-purple-500/50 focus:outline-none resize-none"
              />
            </div>
            
            <div className="p-6 border-t border-[#1F1F1F] flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowApprovalModal(false);
                  setApprovalComments('');
                }}
                className="px-4 py-2 bg-[#1F1F1F] text-[#A0A0A0] rounded-lg hover:bg-[#2F2F2F]"
              >
                Cancel
              </button>
              <button
                onClick={() => handleApproval(selectedResponse.id, approvalAction, approvalComments)}
                disabled={approvalAction === 'reject' && !approvalComments.trim()}
                className={`px-4 py-2 rounded-lg font-medium disabled:opacity-50 ${
                  approvalAction === 'approve'
                    ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/30'
                    : 'bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30'
                }`}
              >
                {approvalAction === 'approve' ? 'Approve' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Sub-components
function StatCard({ label, value, icon: Icon, color }) {
  const colorClasses = {
    purple: 'bg-purple-500/20 text-purple-400',
    amber: 'bg-amber-500/20 text-amber-400',
    emerald: 'bg-emerald-500/20 text-emerald-400',
    red: 'bg-red-500/20 text-red-400'
  };

  return (
    <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon size={18} />
        </div>
        <div>
          <div className="text-2xl font-bold text-white">{value}</div>
          <div className="text-xs text-[#6B6B6B]">{label}</div>
        </div>
      </div>
    </div>
  );
}

function ResponseCard({ response, onApprove, onReject, onView, processingResponse }) {
  const isProcessing = processingResponse === response.id;
  const qualityScore = response.dataQualityAssessment?.overallScore || 0;
  const isPending = response.approval?.status === 'pending';
  const isApproved = response.approval?.status === 'approved';
  const isRejected = response.approval?.status === 'rejected';

  return (
    <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-6 hover:border-purple-500/30 transition-colors">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-base font-medium text-white">{response.supplierName}</h3>
            <StatusBadge status={response.approval?.status || 'pending'} />
          </div>
          
          <div className="flex items-center gap-4 text-sm text-[#6B6B6B]">
            <span>Submitted: {new Date(response.submittedAt).toLocaleDateString()}</span>
            {response.submittedBy?.contactEmail && (
              <span>by {response.submittedBy.contactEmail}</span>
            )}
          </div>
        </div>

        {/* Quality Score */}
        <div className="text-center">
          <div className={`w-14 h-14 rounded-full border-2 flex items-center justify-center ${
            qualityScore >= 80 ? 'border-emerald-500 text-emerald-400' :
            qualityScore >= 50 ? 'border-amber-500 text-amber-400' :
            'border-red-500 text-red-400'
          }`}>
            <span className="text-lg font-bold">{qualityScore}%</span>
          </div>
          <p className="text-[10px] text-[#6B6B6B] mt-1">Quality</p>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          {isPending && (
            <>
              <button
                onClick={onApprove}
                disabled={isProcessing}
                className="flex items-center gap-2 px-3 py-2 bg-emerald-500/20 text-emerald-400 rounded-lg hover:bg-emerald-500/30 disabled:opacity-50"
              >
                {isProcessing ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />}
                Approve
              </button>
              <button
                onClick={onReject}
                disabled={isProcessing}
                className="flex items-center gap-2 px-3 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 disabled:opacity-50"
              >
                <X size={14} />
                Reject
              </button>
            </>
          )}
          <button
            onClick={onView}
            className="p-2 rounded-lg hover:bg-[#1F1F1F] text-[#6B6B6B] hover:text-white"
            title="View Details"
          >
            <Eye size={16} />
          </button>
        </div>
      </div>

      {/* Response Summary */}
      {response.responses && (
        <div className="mt-4 pt-4 border-t border-[#1F1F1F]">
          <p className="text-xs text-[#6B6B6B] mb-2">Response Summary:</p>
          <div className="grid grid-cols-3 gap-3">
            {response.responses.slice(0, 3).map((r, idx) => (
              <div key={idx} className="p-3 bg-[#0A0A0A] rounded-lg">
                <p className="text-[10px] text-[#6B6B6B] truncate">{r.questionId}</p>
                <p className="text-sm text-white truncate">{r.value || '-'}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Approval Info */}
      {(isApproved || isRejected) && response.approval && (
        <div className={`mt-4 p-3 rounded-lg ${
          isApproved ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-red-500/10 border border-red-500/20'
        }`}>
          <div className="flex items-center justify-between text-sm">
            <span className={isApproved ? 'text-emerald-400' : 'text-red-400'}>
              {isApproved ? 'Approved' : 'Rejected'} on {new Date(response.approval.approvedAt || response.approval.rejectedAt).toLocaleDateString()}
            </span>
            {response.approval.approvedBy?.email && (
              <span className="text-[#6B6B6B]">by {response.approval.approvedBy.email}</span>
            )}
          </div>
          {(response.approval.comments || response.approval.reason) && (
            <p className="text-xs text-[#A0A0A0] mt-1">
              {response.approval.comments || response.approval.reason}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const config = {
    pending: { label: 'Pending', bg: 'bg-amber-500/20', text: 'text-amber-400' },
    approved: { label: 'Approved', bg: 'bg-emerald-500/20', text: 'text-emerald-400' },
    rejected: { label: 'Rejected', bg: 'bg-red-500/20', text: 'text-red-400' }
  }[status] || { label: status, bg: 'bg-[#2F2F2F]', text: 'text-[#6B6B6B]' };

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
}
